// src/app/pages/ProfilePage/index.tsx
import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  User, Send, Youtube, Instagram, Music2, Info, Tag, Check, X, Plus,
  Settings, Globe, Sun, Moon, Monitor, Copy, Gift, Ticket, Sparkles, Star,
} from "lucide-react";
import { useNavigate } from "react-router";
import { observer } from "mobx-react-lite";
import { TokenBalance, ProfileOverviewSection } from "src/app/core/ProfileComp/profilePage/overview";
import { FAQSection } from "../faq";
import {
  emitNetworkStatus, resolveNetworkState, NetworkState, runWithNetworkLifecycle,
  type NetworkLifecycleCallbacks,
} from "@/app/shared/lifecycle";
import { ProfileOverviewController } from "./controller/ProfileOverviewController";
import { ProfileViewModel } from "@/app/core/tockensPaymentLogic/ProfileViewModel";
import { AppStore } from "@/app/shared/store/AppStore";
import { SubscriptionPurchasedModal } from "@/app/features/profile/subscription/components/SubscriptionPurchasedModal";
import { AboutAppModal } from "@/app/core/ProfileComp/about/AboutAppModal";
import { PaymentsApi } from "@/app/data/profilPayment/api/PaymentsApi";
import { useTranslation } from "@/app/core/ui/theme/LanguageProvider";
import { useTheme } from "@/app/core/ui/theme/ThemeProvider";

interface ProfilePageProps { lifecycle?: NetworkLifecycleCallbacks; }

export const ProfilePage = observer(function ProfilePage({ lifecycle }: ProfilePageProps) {
  const navigate = useNavigate();
  const { t, lang, setLang } = useTranslation();
  const { theme, setTheme } = useTheme();
  const appStore = AppStore.getInstance();
  const overviewController = useMemo(() => new ProfileOverviewController(), []);
  const profileViewModel = ProfileViewModel.getInstance();
  const [isSettingsOpen, setSettingsOpen] = useState(false);
  const [isAboutOpen, setAboutOpen] = useState(false);
  const [promoCode, setPromoCode] = useState("");
  const [isPromoLoading, setIsPromoLoading] = useState(false);
  const [promoResult, setPromoResult] = useState<{ success: boolean; message: string } | null>(null);
  const [copiedReferral, setCopiedReferral] = useState(false);

  const profileError = profileViewModel.profileError;
  const balanceError = profileViewModel.balanceError;
  const isProfileLoading = profileViewModel.isLoadingProfile || profileViewModel.isLoadingBalance;

  const tgUser = (window as any).Telegram?.WebApp?.initDataUnsafe?.user;
  const tgDisplayName = tgUser ? `${tgUser.first_name || ''} ${tgUser.last_name || ''}`.trim() : '';
  const tgUsername = tgUser?.username ? `${tgUser.username}` : '';
  const tgPhotoUrl = tgUser?.photo_url || '';

  const displayName = tgDisplayName ||
    (profileViewModel.displayName !== 'Пользователь' ? profileViewModel.displayName : '') ||
    'Пользователь';

  const displayUsername = profileViewModel.displayUsername || tgUsername || '@unknown';

  const headerViewData = overviewController.buildHeaderViewData({
    displayName,
    displayUsername,
    fallbackUsername: displayUsername,
    balance: profileViewModel.balance,
    fallbackBalance: profileViewModel.profile?.currentTokens ?? 0,
    profilePhotoUrl: profileViewModel.profile?.photoUrl || tgPhotoUrl || undefined,
  });

  const myReferralCode = profileViewModel.profile?.referralCode || '';

  useEffect(() => {
    const hasError = Boolean(profileError || balanceError);
    const isLoading = isProfileLoading;
    const state = resolveNetworkState({ isLoading, hasError });
    emitNetworkStatus(lifecycle, {
      state,
      error: state === NetworkState.Error ? [profileError, balanceError].find(Boolean) : undefined,
      source: "ProfilePage",
      meta: {},
    });
  }, [lifecycle, profileError, balanceError, isProfileLoading]);

  useEffect(() => {
    runWithNetworkLifecycle({
      source: "ProfileLoadAll",
      lifecycle,
      task: () => profileViewModel.loadAll(),
    }).catch(() => {});
  }, [profileViewModel, lifecycle]);

  const handlePromoRedeem = async () => {
    const trimmed = promoCode.trim();
    if (!trimmed) {
      setPromoResult({ success: false, message: t('promoEmpty') });
      return;
    }
    setIsPromoLoading(true);
    setPromoResult(null);
    try {
      const paymentsApi = new PaymentsApi();
      const result = await paymentsApi.redeemPromo(trimmed);
      setPromoResult({
        success: true,
        message: result.alreadyRedeemed
          ? `${t('promoAlreadyRedeemed')} +${result.tokenAmount}`
          : `${t('promoSuccess')} +${result.tokenAmount}`,
      });
      await profileViewModel.loadAll();
    } catch (error: any) {
      setPromoResult({ success: false, message: error.message || t('promoError') });
    } finally {
      setIsPromoLoading(false);
    }
  };

  const handleCopyReferral = async () => {
    if (!myReferralCode) return;
    try {
      await navigator.clipboard.writeText(myReferralCode);
      setCopiedReferral(true);
      setTimeout(() => setCopiedReferral(false), 1600);
    } catch (e) {
      console.error('[ProfilePage] Copy referral failed', e);
    }
  };

  const themeOptions = [
    { value: 'light', icon: Sun },
    { value: 'dark', icon: Moon },
    { value: 'auto', icon: Monitor },
  ] as const;

  const SettingsModal = () => (
    <AnimatePresence>
      {isSettingsOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
          onClick={() => setSettingsOpen(false)}
        >
          <motion.div
            initial={{ scale: 0.95, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ type: "spring", damping: 20 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm bg-white dark:bg-gray-900 rounded-2xl p-6 shadow-xl border border-border"
          >
            <h2 className="text-xl font-bold text-foreground mb-6 text-center">{t('settings')}</h2>
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Globe className="h-5 w-5 text-violet-600" />
                <span className="text-sm font-medium text-muted-foreground">{t('language')}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {(['ru', 'en'] as const).map((code) => {
                  const isActive = lang === code;
                  return (
                    <button
                      key={code}
                      onClick={() => setLang(code)}
                      className={`py-3 px-4 rounded-xl border transition-all font-medium ${
                        isActive
                          ? 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white border-transparent shadow-lg shadow-violet-500/30'
                          : 'bg-muted text-foreground hover:bg-muted/80'
                      }`}
                    >
                      {code === 'ru' ? 'Русский' : 'English'}
                    </button>
                  );
                })}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <>
      <div className="flex-1 min-h-0 overflow-y-auto pb-32" style={{ backgroundColor: 'transparent' }}>
        {/* Стеклянный хедер */}
<div
  className="sticky top-0 z-40 mx-2 mt-2 rounded-2xl backdrop-blur-xl bg-gradient-to-r 
  from-violet-500/10 via-white/40 to-indigo-500/10 dark:from-violet-500/10 dark:via-gray-900/50 
  dark:to-indigo-500/10 border border-white/30 dark:border-white/10 shadow-lg shadow-violet-500/10"
>
  <div className="px-4 py-4">
    <div className="flex flex-wrap items-center gap-3">
      <div className="flex items-center gap-3">
        {headerViewData.profilePhotoUrl ? (
          <img
            src={headerViewData.profilePhotoUrl}
            alt={headerViewData.displayName}
            className="h-10 w-10 rounded-full border-2 border-white/70 dark:border-white/20 object-cover shadow-md md:h-12 md:w-12"
          />
        ) : (
          <motion.div
            className="flex h-10 w-10 items-center justify-center rounded-full 
            bg-gradient-to-br from-violet-500 to-purple-600 md:h-12 md:w-12 shadow-lg shadow-violet-500/30"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
          >
            <User className="h-5 w-5 text-white md:h-6 md:w-6" />
          </motion.div>
        )}
        <div>
          <div className="text-sm font-bold text-foreground md:text-base">
            {headerViewData.displayName}
          </div>
          <div className="text-xs text-muted-foreground">
            {headerViewData.displayUsername}
          </div>
        </div>
      </div>
      <div className="flex-1 flex justify-end items-center gap-2">
        <div
          onClick={() => navigate("/top-up")}
          className="cursor-pointer transition-all hover:scale-105 flex items-center 
          gap-1.5 whitespace-nowrap rounded-full bg-white/60 dark:bg-white/5 border border-gray-300 
          dark:border-white/10 px-3 py-1.5 shadow-sm hover:bg-white/80 dark:hover:bg-white/10"
          title={t("topUp")}
        >
          <TokenBalance balance={headerViewData.displayBalance} />
          <span className="text-muted-foreground shrink-0 transition-transform hover:scale-110">
            <Plus size={16} />
          </span>
        </div>
      </div>
    </div>
  </div>
</div>

        {/* Контент */}
        <div className="px-4 pt-2 pb-6">
          <div className="mx-auto max-w-7xl">
            {/* Переключатели темы и настройки */}
            <div className="flex items-stretch gap-2 mb-6">
              <div className="flex flex-1 gap-2">
                {themeOptions.map(({ value, icon: Icon }) => {
                  const isActive = theme === value;
                  return (
                    <button
                      key={value}
                      onClick={() => setTheme(value)}
                      className={`flex-1 flex items-center justify-center p-3 rounded-2xl border  ${
                        isActive
                          ? 'bg-gradient-to-br from-violet-600 to-indigo-600 text-white border-transparent shadow-lg shadow-violet-500/30'
                          : 'bg-white/60 dark:bg-white/5 border-gray-300 dark:border-white/10 text-muted-foreground hover:bg-white/80 dark:hover:bg-white/10'
                      }`}
                      style={{ transform: 'none' }}
                    >
                      <Icon size={20} />
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => setSettingsOpen(true)}
                className="p-3 rounded-2xl bg-white/60 dark:bg-white/5 border border-gray-300 dark:border-white/10 text-foreground hover:bg-white/80 dark:hover:bg-white/10 transition-colors flex items-center justify-center"
                aria-label={t('settings')}
              >
                <Settings size={20} />
              </button>
            </div>

            <ProfileOverviewSection profileError={profileError} balanceError={balanceError} />

            {/* Креативная секция: промокод + реферальный код */}
            <div className="mt-6 max-w-md mx-auto md:mx-0">
              <div className="relative overflow-hidden rounded-2xl border border-violet-200/60 dark:border-violet-300/20 bg-gradient-to-br from-violet-200/60 via-white/80 to-indigo-200/60 dark:from-violet-900/40 dark:via-gray-900/60 dark:to-indigo-900/40 backdrop-blur-md p-5 shadow-xl shadow-violet-500/20">
                {/* Плавающие декоративные частицы */}
                <div className="absolute top-0 left-0 w-full h-full pointer-events-none">
                  <motion.span
                    className="absolute top-4 left-4 text-violet-400 dark:text-violet-300"
                    animate={{ y: [0, -10, 0], opacity: [0.3, 0.8, 0.3], rotate: [0, 10, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Sparkles size={18} />
                  </motion.span>
                  <motion.span
                    className="absolute bottom-6 right-6 text-indigo-400 dark:text-indigo-300"
                    animate={{ y: [0, 10, 0], opacity: [0.2, 0.9, 0.2], rotate: [0, -10, 0] }}
                    transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Star size={20} />
                  </motion.span>
                  <motion.span
                    className="absolute top-1/2 left-1/2 text-fuchsia-400 dark:text-fuchsia-300"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.2, 0.7, 0.2] }}
                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <Sparkles size={16} />
                  </motion.span>
                </div>

                <div className="relative grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                  {/* Промокод */}
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-fuchsia-500 text-white shadow-md">
                        <Ticket size={16} />
                      </div>
                      <h3 className="text-sm font-bold text-foreground">{t('promoCode')}</h3>
                    </div>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                        <input
                          value={promoCode}
                          onChange={(e) => setPromoCode(e.target.value)}
                          placeholder="WELCOME50"
                          disabled={isPromoLoading}
                          className="w-full rounded-xl bg-white/60 dark:bg-white/5 border border-violet-200/60 dark:border-violet-300/20 pl-9 pr-3 py-2.5 text-sm text-foreground outline-none placeholder:text-muted-foreground focus:border-accent"
                        />
                      </div>
                      <button
                        onClick={handlePromoRedeem}
                        disabled={isPromoLoading || !promoCode.trim()}
                        className="px-4 py-2 rounded-xl bg-gradient-to-br from-violet-600 to-fuchsia-600 hover:from-violet-700 hover:to-fuchsia-700 active:scale-95 shadow-md text-white text-sm font-medium transition-all"
                      >
                        {isPromoLoading ? '...' : t('apply')}
                      </button>
                    </div>
                    {promoResult && (
                      <motion.div
                        initial={{ opacity: 0, y: -4 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex items-center gap-2 text-sm mt-2 ${
                          promoResult.success ? 'text-emerald-500' : 'text-red-500'
                        }`}
                      >
                        {promoResult.success ? <Check size={16} /> : <X size={16} />}
                        <span>{promoResult.message}</span>
                      </motion.div>
                    )}
                  </div>

                  {/* Реферальный код */}
                  <div>
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-sky-500 text-white shadow-md">
                          <Gift size={16} />
                        </div>
                        <h3 className="text-sm font-bold text-foreground">{t('yourReferralCode')}</h3>
                      </div>
                      <button
                        onClick={handleCopyReferral}
                        disabled={!myReferralCode}
                        className={`p-1.5 rounded-lg hover:bg-white/20 text-muted-foreground transition-colors ${!myReferralCode ? 'opacity-40 cursor-not-allowed' : ''}`}
                        aria-label={t('copy')}
                      >
                        {copiedReferral ? <Check size={16} className="text-emerald-500" /> : <Copy size={16} />}
                      </button>
                    </div>
                    <div className="flex items-center gap-2 bg-white/40 dark:bg-white/5 border border-indigo-200/60 dark:border-indigo-300/20 rounded-xl px-3 py-2.5">
                      <span className="flex-1 text-sm text-foreground truncate font-mono">
                        {myReferralCode || '—'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* FAQ */}
            <div className="mt-8"><FAQSection isLoading={isProfileLoading} /></div>

            {/* Поддержка и соцсети */}
            <div className="mt-8 flex flex-col items-center gap-3">
              <a href="https://t.me/Omega_wave_Bot" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-xl bg-white/60 dark:bg-white/5 border border-gray-300 dark:border-white/10 px-5 py-2.5 text-sm font-medium text-foreground hover:bg-white/80 dark:hover:bg-white/10 transition-colors">
                <Send size={16} className="text-muted-foreground" /> {t("support")}
              </a>
              <div className="flex items-center gap-4">
                <a href="https://www.youtube.com/@Kab-Co" target="_blank" rel="noopener noreferrer" className="p-2 rounded-xl bg-white/60 dark:bg-white/5 border border-gray-300 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" aria-label="YouTube"><Youtube size={20} className="text-foreground" /></a>
                <a href="https://www.instagram.com/omega.coded/" target="_blank" rel="noopener noreferrer" className="p-2 rounded-xl bg-white/60 dark:bg-white/5 border border-gray-300 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" aria-label="Instagram"><Instagram size={20} className="text-foreground" /></a>
                <a href="https://www.tiktok.com/@omega.coded" target="_blank" rel="noopener noreferrer" className="p-2 rounded-xl bg-white/60 dark:bg-white/5 border border-gray-300 dark:border-white/10 hover:bg-white/80 dark:hover:bg-white/10 transition-colors" aria-label="TikTok"><Music2 size={20} className="text-foreground" /></a>
              </div>
            </div>

            {/* О приложении */}
            <div className="mt-4 flex justify-center">
              <button onClick={() => setAboutOpen(true)} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors underline underline-offset-4">
                <Info size={16} /> {t("aboutApp")}
              </button>
            </div>
          </div>
        </div>
      </div>

      {appStore.pendingModal?.type === "subscription_purchased" && (
        <SubscriptionPurchasedModal
          productName={appStore.pendingModal.productName}
          features={appStore.pendingModal.features}
          onClose={() => appStore.clearPendingModal()}
        />
      )}
      <AboutAppModal open={isAboutOpen} onClose={() => setAboutOpen(false)} />
      <SettingsModal />
    </>
  );
});